/**
 * themeColors.ts — Runtime CSS-variable reader for chart libraries.
 *
 * Why this exists:
 *   Tailwind utilities resolve CSS vars at build time via the
 *   `rgb(var(--x) / <alpha-value>)` pattern. Chart libraries (Recharts,
 *   react-circular-progressbar) take colors as plain string props
 *   (`stroke="#76D69D"`, `pathColor="rgba(118,214,157,0.4)"`). They cannot
 *   consume CSS vars directly. This module bridges the two: at render time
 *   it reads the active theme's CSS variable values via getComputedStyle()
 *   and returns concrete hex/rgb/rgba strings the chart libs accept.
 *
 * Critical timing rule:
 *   Call `getThemeColors()` (and `getScoreColors()`) INSIDE component render
 *   functions, never at module top level. `getComputedStyle()` reads the
 *   CURRENT theme's CSS variable values. Module top-level execution may run
 *   before `<html>.light` is set, returning stale dark-mode values that
 *   won't update on theme toggle.
 *
 *   To trigger re-renders on theme toggle, parent chart components should
 *   subscribe to `useTheme()` so children re-render when the value changes.
 */

/**
 * Read a CSS variable's bare RGB-channel string from :root, e.g. "118 214 157".
 * Returns "" if the var is missing or we're running outside a browser (SSR).
 */
export function cssVar(name: string): string {
  if (typeof document === 'undefined') return ''
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/**
 * Convert a `--color-x` channel var into a chart-library-friendly color string.
 *
 * @param varName  CSS var name including leading `--`, e.g. "--color-primary-500".
 * @param alpha    Optional alpha 0-1; produces `rgba(R, G, B, A)` when supplied.
 * @returns        `rgb(R, G, B)`, `rgba(R, G, B, A)`, or `"transparent"` when the
 *                 variable is missing/malformed (safe default — won't crash SVG).
 */
export function toRgb(varName: string, alpha?: number): string {
  const channels = cssVar(varName)
  // Whitespace-tolerant split — bare-channel format is "R G B" but editors
  // sometimes leave extra spaces or tabs around the value.
  const [r, g, b] = channels.split(/\s+/).filter(Boolean)
  if (!r || !g || !b) return 'transparent'

  return alpha === undefined
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export interface ThemeColors {
  // Surfaces (theme-driven)
  surface: string             // dark-800: cards, elevated surfaces
  surfaceAlt: string          // dark-700: hover, tertiary
  surfaceMuted: string        // dark-900: page bg, deep wells
  border: string              // dark-600: borders, axis lines
  muted: string               // dark-500: muted UI

  // Chart infrastructure (theme-driven)
  chartGrid: string           // CartesianGrid stroke
  chartAxis: string           // axis line / tick line stroke
  chartAxisLabel: string      // axis tick label fill
  chartActiveDotFill: string  // activeDot fill — sits on top of the line, blends into surface
  chartTooltipBg: string      // custom tooltip background = surface
  chartTooltipBorder: string  // custom tooltip border = border

  // Brand (theme-driven)
  primary: string             // primary-500: Soft Green (dark) / deeper green (light)
  primaryGlow: string         // primary-500 @ 0.4 alpha — glow effects
  secondaryBlue: string       // secondary-blue-300: Horizon Blue
  secondarySand: string       // secondary-sand-300: Warm Sand

  // Severity — semantic, theme-INVARIANT (red is red in both modes).
  // Phase 12 may swap these for brand-tuned variants (e.g. critical = #E57373)
  // once the visual sweep confirms the values.
  critical: string
  criticalGlow: string
  high: string
  highGlow: string
  medium: string
  mediumGlow: string
  low: string
  lowGlow: string
  info: string
  infoGlow: string

  // Score (CircularProgressbar buildStyles)
  trailColor: string          // dark-700: ring trail behind score arc
  scoreGood: string           // primary-500: >= 80
  scoreGoodGlow: string
  scoreGoodText: string       // brighter tint for numeral readability
  scoreFair: string           // warm sand: 60-79
  scoreFairGlow: string
  scoreFairText: string
  scorePoor: string           // critical red: < 60
  scorePoorGlow: string
  scorePoorText: string
}

const SEVERITY = {
  critical:    '#ef4444',
  criticalGlow:'rgba(239, 68, 68, 0.4)',
  high:        '#f97316',
  highGlow:    'rgba(249, 115, 22, 0.4)',
  medium:      '#eab308',
  mediumGlow:  'rgba(234, 179, 8, 0.4)',
  low:         '#22c55e',
  lowGlow:     'rgba(34, 197, 94, 0.4)',
  info:        '#3b82f6',
  infoGlow:    'rgba(59, 130, 246, 0.4)',
} as const

/**
 * Snapshot the active theme's chart-relevant colors.
 *
 * IMPORTANT: call inside a component render function, not at module scope.
 * Each invocation reads CSS vars fresh, so theme toggles take effect on the
 * next render of any component that calls this.
 */
export function getThemeColors(): ThemeColors {
  const surface       = toRgb('--color-dark-800')
  const surfaceAlt    = toRgb('--color-dark-700')
  const border        = toRgb('--color-dark-600')
  const primary       = toRgb('--color-primary-500')
  const primaryGlow   = toRgb('--color-primary-500', 0.4)
  const secondarySand = toRgb('--color-secondary-sand-300')

  return {
    // Surfaces
    surface,
    surfaceAlt,
    surfaceMuted:    toRgb('--color-dark-900'),
    border,
    muted:           toRgb('--color-dark-500'),

    // Chart infrastructure (--color-chart-* added in Phase 1)
    chartGrid:          toRgb('--color-chart-grid'),
    chartAxis:          border,
    chartAxisLabel:     toRgb('--color-chart-axis-label'),
    chartActiveDotFill: toRgb('--color-chart-active-dot'),
    chartTooltipBg:     surface,
    chartTooltipBorder: border,

    // Brand
    primary,
    primaryGlow,
    secondaryBlue:  toRgb('--color-secondary-blue-300'),
    secondarySand,

    // Severity — semantic, theme-invariant
    ...SEVERITY,

    // Score gauge: good = brand primary, fair = warm sand (harmonizes with
    // the iuvo palette better than amber), poor = severity-critical red.
    trailColor:      surfaceAlt,
    scoreGood:       primary,
    scoreGoodGlow:   primaryGlow,
    scoreGoodText:   toRgb('--color-primary-400'),
    scoreFair:       secondarySand,
    scoreFairGlow:   toRgb('--color-secondary-sand-300', 0.4),
    scoreFairText:   toRgb('--color-secondary-sand-200'),
    scorePoor:       SEVERITY.critical,
    scorePoorGlow:   SEVERITY.criticalGlow,
    scorePoorText:   '#fca5a5', // red-300, for numeral on dark surface
  }
}

export interface ScoreColorPair {
  color: string
  glow: string
  text: string
  trail: string
}

/**
 * Bracket a numeric score (0-100) into the matching theme color set.
 * Mirrors the shape used by `complianceColors.ts` so call sites can swap in
 * cleanly. Brackets: >= 80 → good, >= 60 → fair, else → poor.
 */
export function getScoreColors(score: number): ScoreColorPair {
  const tc = getThemeColors()
  const tier =
    score >= 80 ? { color: tc.scoreGood, glow: tc.scoreGoodGlow, text: tc.scoreGoodText } :
    score >= 60 ? { color: tc.scoreFair, glow: tc.scoreFairGlow, text: tc.scoreFairText } :
                  { color: tc.scorePoor, glow: tc.scorePoorGlow, text: tc.scorePoorText }
  return { ...tier, trail: tc.trailColor }
}
